import { useState, useEffect } from "react";
import {
  uploadBankStatements,
  getBankNames,
  getAccountNumbers,
  getFileTypes,
} from "../Api/BankStatements";
import { Container, Form, Button, Row, Col, Accordion, AccordionItem, AccordionBody } from "react-bootstrap";
import { getAllHouseBanks } from "../Api/HouseBank";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";
import { useAuth } from "../Context/AuthContext";

import "../Styling/Title.css";
import "../Styling/Accordion.css";
import "../Styling/SaveButton.css";
import "../Styling/ResetButton.css";

const UploadBankStatements = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [houseBanksLoading, setHouseBanksLoading] = useState(false);

  const { user } = useAuth();

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;

  const [bankNames, setBankNames] = useState([]);
  const [accountNumbers, setAccountNumbers] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [houseBanks, setHouseBanks] = useState([]);
  const [filteredHouseBanks, setFilteredHouseBanks] = useState([]);

  const [formData, setFormData] = useState({
    bankName: "",
    accountNo: "",
    fileType: "",
    housebankId: "",
    file: null,
  });

  useEffect(() => {
    const fetchDropdownData = async () => {
      setDropdownLoading(true);
      try {
        const [bankNamesData, fileTypesData, houseBanksData] =
          await Promise.all([
            getBankNames(user?.organisationId),
            getFileTypes(),
            getAllHouseBanks(user?.organisationId),
          ]);

        setBankNames(bankNamesData);
        setFileTypes(fileTypesData);
        setHouseBanks(houseBanksData);
      } catch (error) {
        toast.error("Error loading dropdown data");
      } finally {
        setDropdownLoading(false);
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (formData.bankName && houseBanks.length > 0) {
      const filtered = houseBanks.filter(
        (bank) =>
          bank.bankName.toLowerCase() === formData.bankName.toLowerCase()
      );

      setFilteredHouseBanks(filtered);

      if (filtered.length === 1) {
        setFormData((prev) => ({
          ...prev,
          housebankId: filtered[0].housebankId,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          housebankId: "",
        }));
      }
    } else {
      setFilteredHouseBanks([]);
      setFormData((prev) => ({
        ...prev,
        housebankId: "",
      }));
    }
  }, [formData.bankName, houseBanks]);

  useEffect(() => {
    const fetchAccountNumbers = async () => {
      if (formData.bankName && userId) {
        setAccountLoading(true);
        try {
          const accounts = await getAccountNumbers(
            formData.bankName,
            userId,
            user?.organisationId
          );

          setAccountNumbers(accounts);
          setFormData((prev) => ({ ...prev, accountNo: "" }));
        } catch (error) {
          toast.error(`Error loading account numbers for ${formData.bankName}`);
          setAccountNumbers([]);
        } finally {
          setAccountLoading(false);
        }
      } else {
        setAccountNumbers([]);
      }
    };

    fetchAccountNumbers();
  }, [formData.bankName, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, file: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.file) {
      toast.error("Please select a file to upload");
      return;
    }

    if (
      !formData.bankName ||
      !formData.accountNo ||
      !formData.fileType ||
      !formData.housebankId
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    if (!userId) {
      toast.error("User information not found. Please login again.");
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      data.append("CSVFile", formData.file);
      data.append("BankName", formData.bankName);
      data.append("AccountNumber", formData.accountNo);
      data.append("FileType", formData.fileType);
      data.append("housebankId", formData.housebankId);

      await uploadBankStatements(data, userId);

      toast.success("Bank Statements Uploaded Successfully");

      setFormData({
        bankName: "",
        accountNo: "",
        fileType: "",
        housebankId: "",
        file: null,
      });
    } catch (error) {
      toast.error(error.message || "Error Uploading Bank Statements");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="container bank-master-fields">

        <h2 className="fields-title">Upload Bank Statements</h2>

        <Form onSubmit={handleSubmit} className="fields-form">

          <Accordion defaultActiveKey="0">
            <AccordionItem eventKey="0">
              <AccordionBody>

                <Row className="g-3">

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Bank Name <span className="text-red">**</span>
                      </Form.Label>

                      <Form.Select
                        name="bankName"
                        value={formData.bankName}
                        onChange={handleChange}
                        required
                        disabled={dropdownLoading}
                      >
                        <option value="">Select Bank</option>

                        {bankNames.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Account No <span className="text-red">**</span>
                      </Form.Label>

                      <Form.Select
                        name="accountNo"
                        value={formData.accountNo}
                        onChange={handleChange}
                        required
                        disabled={!formData.bankName || accountLoading}
                      >
                        <option value="">Select Account</option>

                        {accountNumbers.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        File Type <span className="text-red">**</span>
                      </Form.Label>

                      <Form.Select
                        name="fileType"
                        value={formData.fileType}
                        onChange={handleChange}
                        required
                        disabled={dropdownLoading}
                      >
                        <option value="">Select File Type</option>

                        {fileTypes.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        House Bank <span className="text-red">**</span>
                      </Form.Label>

                      <Form.Select
                        name="housebankId"
                        value={formData.housebankId}
                        onChange={handleChange}
                        required
                        disabled={!formData.bankName || dropdownLoading}
                      >
                        <option value="">Select House Bank</option>

                        {filteredHouseBanks.map((bank) => (
                          <option key={bank.housebankId} value={bank.housebankId}>
                            {bank.bankName} - {bank.housebankId}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xl={4} lg={4} md={6} sm={12}>
                    <Form.Group>
                      <Form.Label>
                        Upload File <span className="text-red">**</span>
                      </Form.Label>

                      <Form.Control
                        type="file"
                        name="file"
                        onChange={handleFileChange}
                        required
                        accept=".csv,.xlsx,.pdf"
                      />
                    </Form.Group>
                  </Col>

                </Row>

              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <div className="fields-btn-container" style={{ marginTop: "18px" }}>

            <Button
              type="button"
              className="fields-reset"
              onClick={() =>
                setFormData({
                  bankName: "",
                  accountNo: "",
                  fileType: "",
                  housebankId: "",
                  file: null,
                })
              }
            >
              Reset
            </Button>

            <Button
              type="submit"
              className="fields-submit"
              disabled={loading}
            >
              {loading ? "Uploading..." : "Upload"}
            </Button>

          </div>

        </Form>
      </div>

      {(loading || dropdownLoading || accountLoading || houseBanksLoading) && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </>
  );
};

export default UploadBankStatements;